/* Favorites stored in localStorage. Each item is a normalized place object with city + ts. */
import { useEffect, useState, useCallback } from 'react';

const KEY = 'puvio:favorites';

export function loadFavorites() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

export function saveFavorites(arr) {
  try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, 200))); } catch {}
}

export function favoriteId(place) {
  return place?.id || `${place?.lat},${place?.lon}::${place?.name || ''}`;
}

export function isFavorited(arr, place) {
  const id = favoriteId(place);
  return arr.some((p) => favoriteId(p) === id);
}

export function useFavorites() {
  const [items, setItems] = useState(() => loadFavorites());

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setItems(loadFavorites());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback((place, city) => {
    setItems((cur) => {
      const id = favoriteId(place);
      const exists = cur.some((p) => favoriteId(p) === id);
      let next;
      if (exists) {
        next = cur.filter((p) => favoriteId(p) !== id);
      } else {
        next = [{ ...place, _city: city ? { name: city.name, country: city.country, lat: city.lat, lon: city.lon } : null, _ts: Date.now() }, ...cur];
      }
      saveFavorites(next);
      return next;
    });
  }, []);

  const remove = useCallback((place) => {
    setItems((cur) => {
      const id = favoriteId(place);
      const next = cur.filter((p) => favoriteId(p) !== id);
      saveFavorites(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => { setItems([]); saveFavorites([]); }, []);

  return { items, toggle, remove, clear, isFavorited: (p) => isFavorited(items, p) };
}
